import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Play, Upload, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  cancelImportRun,
  commitImportRun,
  createImportRun,
  getImportRun,
  getImportRunReport,
  listImportAccounts,
  startImportFetch,
  updateImportRunMapping,
  DATE_RANGED_ENTITIES,
  IMPORT_ENTITIES,
  IMPORT_ENTITY_LABELS,
  IMPORT_STATUS_LABELS,
  SKIP_REASON_LABELS,
  isImportRunActive,
  type ImportEntity,
  type ImportRunDetail,
} from '@/services/import.service';
import { DatePicker } from '@/components/ui/DatePicker';

/**
 * The Zenoti import wizard.
 *
 * Setup and mapping, then a fetch that stages everything and writes nothing,
 * then a review, then the commit. The fetch is paced by Zenoti's limit of 60
 * calls a minute with a 7-day cap on appointment and sales queries, so it runs
 * for as long as it needs to and reports a phase rather than a percentage —
 * there is no honest ETA to show.
 */

type Step = 'setup' | 'mapping' | 'progress' | 'review' | 'report';

function stepForStatus(run: ImportRunDetail | undefined): Step {
  if (!run) return 'setup';
  if (run.status === 'pending') {
    return Object.keys(run.centerFacilityMap).length > 0 ? 'mapping' : 'mapping';
  }
  if (run.status === 'fetching') return 'progress';
  if (run.status === 'preview_ready') return 'review';
  return 'report';
}

export function ZenotiImportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const runIdParam = searchParams.get('runId');
  const runId = runIdParam ? Number(runIdParam) : null;

  // Setup form
  const [accountId, setAccountId] = useState<number | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [entities, setEntities] = useState<ImportEntity[]>(['service', 'employee', 'guest']);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Mapping form: Zenoti center id -> Prospera facility id
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [confirmCommit, setConfirmCommit] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ['admin-import-accounts'],
    queryFn: listImportAccounts,
  });

  const { data: run, isLoading } = useQuery({
    queryKey: ['admin-import-run', runId],
    queryFn: () => getImportRun(runId as number),
    enabled: runId !== null,
    refetchInterval: (query) => {
      const current = query.state.data as ImportRunDetail | undefined;
      return current && isImportRunActive(current.status) ? 4000 : false;
    },
  });

  const { data: report } = useQuery({
    queryKey: ['admin-import-report', runId],
    queryFn: () => getImportRunReport(runId as number, { perPage: 100 }),
    enabled: runId !== null && run?.status === 'completed',
  });

  // Seed the mapping form once the run's centers are known.
  useEffect(() => {
    if (run && Object.keys(run.centerFacilityMap).length > 0) {
      setMapping(run.centerFacilityMap);
    }
  }, [run?.importRunId, run?.centerFacilityMap]);

  const step = stepForStatus(run);

  const createRun = useMutation({
    mutationFn: createImportRun,
    onSuccess: (result) => {
      toast.success('Credentials accepted. Map each Zenoti center to a facility.');
      setApiKey('');
      setSearchParams({ runId: String(result.importRunId) });
      queryClient.invalidateQueries({ queryKey: ['admin-import-runs'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const saveMapping = useMutation({
    mutationFn: (map: Record<string, number>) => updateImportRunMapping(runId as number, map),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-import-run', runId] }),
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const beginFetch = useMutation({
    mutationFn: () => startImportFetch(runId as number),
    onSuccess: () => {
      toast.success('Fetching from Zenoti. This can take a while on a large history.');
      queryClient.invalidateQueries({ queryKey: ['admin-import-run', runId] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const commit = useMutation({
    mutationFn: () => commitImportRun(runId as number),
    onSuccess: () => {
      toast.success('Writing records into the destination account.');
      queryClient.invalidateQueries({ queryKey: ['admin-import-run', runId] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const cancel = useMutation({
    mutationFn: () => cancelImportRun(runId as number),
    onSuccess: () => {
      toast.success('Run cancelled and stored credentials purged.');
      queryClient.invalidateQueries({ queryKey: ['admin-import-run', runId] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const needsDateRange = entities.some((entity) => DATE_RANGED_ENTITIES.includes(entity));

  const canCreate =
    accountId !== null &&
    apiKey.trim().length >= 8 &&
    entities.length > 0 &&
    (!needsDateRange || (dateFrom !== '' && dateTo !== ''));

  const accountOptions = useMemo(
    () => (accounts ?? []).map((a) => ({ value: a.accountId, label: a.name || `Account ${a.accountId}` })),
    [accounts],
  );

  const facilityOptions = useMemo(() => {
    // Facilities arrive with the create response and the centers endpoint; the
    // run detail carries the mapping, so the option list comes from whichever
    // response is freshest in the cache.
    const created = createRun.data?.facilities ?? [];
    return created.map((f) => ({ value: f.facilityId, label: f.name || `Facility ${f.facilityId}` }));
  }, [createRun.data]);

  const centers = createRun.data?.centers ?? [];

  const plannedCounts = run?.counts?.planned ?? {};
  const outcomeCounts = run?.counts?.outcome ?? {};
  const skipReasons = run?.counts?.skipReasons ?? {};

  return (
    // p-6 encloses PageHeader — see the note in ImportAdmin.tsx.
    <div className="p-6 space-y-6">
      <PageHeader
        title="Import from Zenoti"
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate('/admin/import')}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              All imports
            </Button>
            {run && !['completed', 'failed', 'cancelled'].includes(run.status) && (
              <Button variant="secondary" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                <X className="mr-1 h-4 w-4" />
                Cancel run
              </Button>
            )}
          </>
        }
      />

      {isLoading && <SpinnerOverlay />}

      {run && (
        <Card>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge>{IMPORT_STATUS_LABELS[run.status] ?? run.status}</Badge>
            <span className="text-slate-600">
              Run #{run.importRunId} into {run.accountName || `account ${run.accountId}`}
            </span>
            {run.progress && <span className="text-slate-500">· {run.progress}</span>}
            {run.hasStoredCredentials && (
              <span className="text-slate-400">· credentials held until this run finishes</span>
            )}
          </div>
          {run.error && <p className="mt-2 text-sm text-danger">{run.error}</p>}
        </Card>
      )}

      {/* ── Setup ─────────────────────────────────────────────────────────── */}
      {!runId && (
        <Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Destination account" required>
              <Select
                options={accountOptions}
                value={accountId}
                onChange={setAccountId}
                searchable
                placeholder="Choose the practice to import into"
              />
            </FormField>

            <FormField
              label="Zenoti API key"
              required
              helpText="Generated in Zenoti under Admin > Setup > Apps. Stored encrypted and purged when the run finishes."
            >
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
              />
            </FormField>
          </div>

          <div className="mt-4">
            <p className="mb-2 text-sm font-medium text-slate-700">What to import</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {IMPORT_ENTITIES.map((entity) => (
                <Checkbox
                  key={entity}
                  label={IMPORT_ENTITY_LABELS[entity]}
                  checked={entities.includes(entity)}
                  onChange={(checked) =>
                    setEntities((current) =>
                      checked ? [...current, entity] : current.filter((e) => e !== entity),
                    )
                  }
                />
              ))}
            </div>
          </div>

          {needsDateRange && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <FormField
                label="History from"
                required
                helpText="Appointments and sales are fetched a week at a time — Zenoti's own limit."
                htmlFor="zenoti-date-from"
              >
                <DatePicker
                  id="zenoti-date-from"
                  value={dateFrom}
                  onChange={setDateFrom}
                />
              </FormField>
              <FormField label="History to" required htmlFor="zenoti-date-to">
                <DatePicker
                  id="zenoti-date-to"
                  value={dateTo}
                  onChange={setDateTo}
                />
              </FormField>
            </div>
          )}

          <div className="mt-6">
            <Button
              disabled={!canCreate || createRun.isPending}
              onClick={() =>
                createRun.mutate({
                  accountID: accountId as number,
                  apiKey: apiKey.trim(),
                  entities,
                  dateFrom: dateFrom || null,
                  dateTo: dateTo || null,
                })
              }
            >
              Check credentials
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* ── Mapping ───────────────────────────────────────────────────────── */}
      {run?.status === 'pending' && centers.length > 0 && (
        <Card>
          <p className="mb-4 text-sm text-slate-600">
            Each Zenoti center becomes a Prospera facility. A center you leave unmapped is not
            imported at all.
          </p>

          <div className="space-y-3">
            {centers.map((center) => (
              <div key={center.centerId} className="grid items-center gap-3 sm:grid-cols-2">
                <div className="text-sm">
                  <span className="font-medium text-slate-900">{center.name || center.centerId}</span>
                  {center.code && <span className="ml-2 text-slate-500">{center.code}</span>}
                </div>
                <Select
                  options={facilityOptions}
                  value={mapping[center.centerId] ?? null}
                  onChange={(value) =>
                    setMapping((current) => {
                      const next = { ...current };
                      if (value === null) {
                        delete next[center.centerId];
                      } else {
                        next[center.centerId] = value as number;
                      }
                      return next;
                    })
                  }
                  clearable
                  placeholder="Not imported"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex gap-2">
            <Button
              variant="secondary"
              disabled={Object.keys(mapping).length === 0 || saveMapping.isPending}
              onClick={() => saveMapping.mutate(mapping)}
            >
              Save mapping
            </Button>
            <Button
              disabled={Object.keys(run.centerFacilityMap).length === 0 || beginFetch.isPending}
              onClick={() => beginFetch.mutate()}
            >
              <Play className="mr-1 h-4 w-4" />
              Start fetching
            </Button>
          </div>
        </Card>
      )}

      {/* ── Fetch in progress ─────────────────────────────────────────────── */}
      {step === 'progress' && (
        <Card>
          <p className="text-sm text-slate-700">
            Fetching from Zenoti. Nothing has been written to {run?.accountName || 'the account'} yet
            — this stages the records so you can review them first.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Zenoti allows 60 calls a minute, so a long history takes a while. You can leave this
            page; the run continues and picks up where it left off.
          </p>
        </Card>
      )}

      {/* ── Review ────────────────────────────────────────────────────────── */}
      {step === 'review' && run && (
        <>
          <Card>
            <p className="mb-4 text-sm text-slate-700">
              Nothing has been written yet. Review what the commit will do, then confirm.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-4">Records</th>
                    <th className="py-2 pr-4">Create new</th>
                    <th className="py-2 pr-4">Link to existing</th>
                    <th className="py-2">Skip</th>
                  </tr>
                </thead>
                <tbody>
                  {IMPORT_ENTITIES.filter((entity) => plannedCounts[entity]).map((entity) => (
                    <tr key={entity} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4">{IMPORT_ENTITY_LABELS[entity]}</td>
                      <td className="py-2 pr-4">{plannedCounts[entity]?.create ?? 0}</td>
                      <td className="py-2 pr-4">{plannedCounts[entity]?.link ?? 0}</td>
                      <td className="py-2">{plannedCounts[entity]?.skip ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {Object.keys(skipReasons).length > 0 && (
              <div className="mt-4 text-sm">
                <p className="mb-1 font-medium text-slate-700">Why records are being skipped</p>
                <ul className="list-inside list-disc text-slate-600">
                  {Object.entries(skipReasons).map(([reason, count]) => (
                    <li key={reason}>
                      {SKIP_REASON_LABELS[reason] ?? reason}: {count}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6">
              <Button onClick={() => setConfirmCommit(true)} disabled={commit.isPending}>
                <Upload className="mr-1 h-4 w-4" />
                Commit into {run.accountName || `account ${run.accountId}`}
              </Button>
            </div>
          </Card>

          {run.preview && (
            <Card>
              <p className="mb-3 text-sm font-medium text-slate-700">Sample records</p>
              <div className="space-y-4">
                {IMPORT_ENTITIES.filter((entity) => run.preview?.[entity]?.length).map((entity) => (
                  <div key={entity}>
                    <p className="mb-1 text-xs uppercase text-slate-500">
                      {IMPORT_ENTITY_LABELS[entity]}
                    </p>
                    <ul className="space-y-1 text-sm text-slate-700">
                      {(run.preview?.[entity] ?? []).map((item) => (
                        <li key={item.externalId} className="flex flex-wrap items-center gap-2">
                          <Badge variant={item.action === 'link' ? 'warning' : 'neutral'}>
                            {item.action}
                          </Badge>
                          <span>{item.summary || item.externalId}</span>
                          {item.matchNotes && (
                            <span className="text-slate-500">— {item.matchNotes}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* ── Outcome ───────────────────────────────────────────────────────── */}
      {step === 'report' && run && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2 pr-4">Records</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Linked</th>
                  <th className="py-2 pr-4">Skipped</th>
                  <th className="py-2">Failed</th>
                </tr>
              </thead>
              <tbody>
                {IMPORT_ENTITIES.filter(
                  (entity) => outcomeCounts[entity] || plannedCounts[entity],
                ).map((entity) => (
                  <tr key={entity} className="border-b border-border last:border-0">
                    <td className="py-2 pr-4">{IMPORT_ENTITY_LABELS[entity]}</td>
                    <td className="py-2 pr-4">{outcomeCounts[entity]?.committed ?? 0}</td>
                    <td className="py-2 pr-4">{outcomeCounts[entity]?.linked ?? 0}</td>
                    <td className="py-2 pr-4">{outcomeCounts[entity]?.skipped ?? 0}</td>
                    <td className="py-2">{outcomeCounts[entity]?.failed ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(report?.items ?? []).some((item) => item.error) && (
            <div className="mt-4 text-sm">
              <p className="mb-1 font-medium text-slate-700">Records that failed</p>
              <ul className="list-inside list-disc text-slate-600">
                {(report?.items ?? [])
                  .filter((item) => item.error)
                  .slice(0, 25)
                  .map((item) => (
                    <li key={item.id}>
                      {item.entityType} {item.externalId}: {item.error}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog
        isOpen={confirmCommit}
        onClose={() => setConfirmCommit(false)}
        onConfirm={() => {
          setConfirmCommit(false);
          commit.mutate();
        }}
        title="Commit this import?"
        message={`This writes the staged records into ${
          run?.accountName || 'the destination account'
        }. Records already imported by an earlier run are skipped, so re-running is safe.`}
        confirmLabel="Commit"
      />
    </div>
  );
}
